import { OrthographicCamera } from '@react-three/drei';
import { ThreeElements, useFrame, useThree } from '@react-three/fiber';
import React, { useRef, memo, FunctionComponent, useEffect, useLayoutEffect } from 'react';
import { Scene, OrthographicCamera as OrthographicCameraImpl } from 'three';
import { IEntityActionTypes, IMode, ISceneActions, ITransCatTypes, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '../../zustand';
import { IActionCategory, IPlaySoundAction, IRoot, ISceneComp, ITrackingTypes } from '../r3f-components/component-data-structure';
import { screenRelativeCameraNeedsUpdate, updateScreenRelativeCameraProperties } from '../r3f-components/utils/screen-relative';
import ScreenRelativeContent from './ScreenRelativeContent';
import { ITransitionPhase } from '../r3f-components/transitions/spec';
import { useGetActiveTrackingType } from '../hoc/hocCustomHooks';
import { Jute } from '../..';

const ScreenRelativeScene: FunctionComponent<ThreeElements['scene']> = ({ children, ...props }) => {
	const screenRelativeCamRef = useRef<OrthographicCameraImpl>(null!);
	const screenRelativeSceneRef = useRef<Scene>(null!);
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const firstSceneId = useDataStore((state) => (state.componentsById?.[state.rootId as string] as IRoot)?.children[0]);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId!);
	const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId!);
	const previousSceneWasMenuScene = useDataStore((store) => (store?.componentsById?.[previousSceneId] as ISceneComp)?.trackingType === ITrackingTypes.noTrackingScreen);
	const currentSceneId = activeSceneId || firstSceneId;
	const activeTrackingType = useGetActiveTrackingType();
	const isTrackingImageVisible = useUserStore(({ isTrackingImageVisible }) => isTrackingImageVisible);
	const isFaceTrackingVisible = useUserStore((state) => state.isFaceTrackingVisible);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const mode = useDataStore(({ mode }) => mode);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const scene = componentsById?.[currentSceneId] as ISceneComp;
	const sceneOnLoadPlaySoundActions = scene?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound) as IPlaySoundAction[] | undefined;
	const {size} = useThree()

	useFrame(({ gl, size }) => {
		const { current: screenRelativeCam } = screenRelativeCamRef;
		if (screenRelativeCam && screenRelativeCameraNeedsUpdate(screenRelativeCam, size)) {
			// console.log('update screenRelative camera!');
			updateScreenRelativeCameraProperties(screenRelativeCam, size);
			screenRelativeCam.updateProjectionMatrix();
			// https://github.com/pmndrs/react-three-fiber/issues/178
			// Update matrix world since the renderer is a frame late
			screenRelativeCam.updateMatrixWorld();
		}

		gl.autoClear = false;
		gl.clearDepth();
		gl.render(screenRelativeSceneRef.current, screenRelativeCam);
		// gl.autoClear = true;
	}, 100);

	useLayoutEffect(() => {
		Jute.onLaunch(() => {
			sceneOnLoadPlaySoundActions?.forEach((action) => {
				let { stopMedia, loop } = action as IPlaySoundAction;
				if (stopMedia) {
					entityActionDispatch?.({
						type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
					});
					entityActionDispatch?.({
						type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
						payload: { id: currentSceneId },
					});
					entityActionDispatch?.({
						type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
					});
				}
				entityActionDispatch?.({
					type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
					payload: { sceneId: currentSceneId, entityIds: [currentSceneId] },
				});
			})
		})
	}, [])

	useEffect(() => {
		if (activeTrackingType === ITrackingTypes.noTrackingScreen) {
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_CATEGORY,
				transCatDict: {
					[currentSceneId]: ITransCatTypes.enter,
				},
			});
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_PHASE,
				transPhaseDict: {
					[currentSceneId]: ITransitionPhase.enterFinish,
				},
			});
			if (previousSceneId) {
				entityActionDispatch?.({
					type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
					payload: { id: previousSceneId },
				});
				sceneOnLoadPlaySoundActions?.forEach((action) => {
					let { stopMedia, loop } = action;
					if (stopMedia) {
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
							payload: { id: currentSceneId },
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
						});
					}
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
						payload: { sceneId: currentSceneId, entityIds: [currentSceneId]},
					});
				})
			}
		}

		// If prevSceneId was a menu type then set to hidden / exitFinish
		if (previousSceneWasMenuScene) {
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_CATEGORY,
				transCatDict: {
					[previousSceneId]: ITransCatTypes.hidden,
				},
			});
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_PHASE,
				transPhaseDict: {
					[previousSceneId]: ITransitionPhase.exitFinish,
				},
			});
		}
	}, [activeSceneId, activeTrackingType, componentsById]);


	const targetLostPromptActive = activeTrackingType === ITrackingTypes.image && !isTrackingImageVisible;
	const placementPromptActive = activeTrackingType === ITrackingTypes.world && placementMode;
	const faceLostPromptActive = (activeTrackingType === ITrackingTypes.face && !isFaceTrackingVisible) && mode === IMode.ar;
	const noTrackingScene = activeTrackingType === ITrackingTypes.noTrackingScreen;

	return (
		<scene ref={screenRelativeSceneRef} {...props} name="ScreenRelativeScene">
			<OrthographicCamera name={'screenRelativeCamera'} ref={screenRelativeCamRef} makeDefault={false} left={-1} right={1} top={1} bottom={-1} zoom={1} />
			<group visible={noTrackingScene || (!placementPromptActive && !targetLostPromptActive && !faceLostPromptActive)} scale={1}>
				<ScreenRelativeContent cameraRef={screenRelativeCamRef} />
				{children}
			</group>
		</scene>
	);
};

export default memo(ScreenRelativeScene);
